import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { baseUrl } from 'utils/axios'
import {
  areDatesSameDay,
  formatParams,
  handleErrors,
  minutesToUTCDate,
  toStartOfUtcDay,
} from 'utils/helpers'
import {
  IPostAttendanceRequest,
  IGetAllAttendanceSingleResponse,
  IPostAttendanceRequestResponse,
  IPostMultipleAttendanceRequest,
} from 'types/api/attendanceApi.interface'
import { IPaginatedResponse } from 'types/api/general.interface'
import { IAttendanceModal } from 'pages/Attendance/AttendanceModal'
import { v4 } from 'uuid'

interface IAttendanceSlice {
  attendance: IGetAllAttendanceSingleResponse[]
  totalCount: number
  totalPages: number
  isLoading: boolean
  noAttendances: boolean
}

const initialState: IAttendanceSlice = {
  attendance: [],
  totalCount: 0,
  totalPages: 0,
  isLoading: false,
  noAttendances: true,
}

// GET: /v1/attendance/management/users
export const getAttendances = createAsyncThunk(
  'attendance/getAttendances',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllAttendanceSingleResponse>> =
        await baseUrl.get('attendance/management/users', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

// POST: /v1/attendance/management/attendance
export const createAttendance = createAsyncThunk(
  'attendance/createAttendance',
  async (state: IAttendanceModal, thunkAPI) => {
    try {
      const req: IPostAttendanceRequest = {
        entryType: !!state.shiftId ? 1 : 2,
        workScheduleId: state.shiftId || null,
        userId: state.userId,
        type: state.attendanceType.value,
        startDate: state.checkIn
          ? minutesToUTCDate(state.checkIn, state.date).toISOString()
          : state.date.toISOString(),
        endDate: state?.checkOut
          ? minutesToUTCDate(state?.checkOut, state.date).toISOString()
          : null,
      }
      const resp: AxiosResponse<IPostAttendanceRequestResponse[]> = await baseUrl.post(
        'attendance/management/attendance',
        req,
      )
      return { userId: state.userId, data: { ...resp.data, startDate: req.startDate } }
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createMultipleAttendance = createAsyncThunk(
  'attendance/createMultipleAttendance',
  async (req: IPostMultipleAttendanceRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPostAttendanceRequestResponse[]> = await baseUrl.post(
        'attendance/management/attendance/multiple',
        req,
      )

      return {
        userIds: req.userIds,
        data: resp.data.map((d) => ({ ...d, startDate: req.startDate })),
      }
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

// DELETE: /v1/attendance/management/attendance/{id}
export const deleteAttendance = createAsyncThunk(
  'attendance/deleteAttendance',
  async ({ id, userId }: { id: number; userId: number }, thunkAPI) => {
    try {
      await baseUrl.delete(`/attendance/management/attendance/${id}`)
      return { id, userId }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

// POST: /v1/attendance/management/import
export const importAttendances = createAsyncThunk(
  'attendance/importAttendances',
  async ({ file, should }: { file: File; should: boolean }, thunkAPI) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('shouldImportNonWorkScheduleAttendance', String(should))
    try {
      const resp: AxiosResponse = await baseUrl.post('attendance/management/import', formData)
      return resp.data
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const attendanceSlice = createSlice({
  name: 'attendance',
  initialState,
  reducers: {
    clearAttendances: (state) => {
      state.attendance = []
      state.totalCount = 0
      state.totalPages = 0
      state.isLoading = false
      state.noAttendances = true
    },
  },
  extraReducers: {
    [getAttendances.pending.type]: (state) => {
      state.isLoading = true
    },
    [getAttendances.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IGetAllAttendanceSingleResponse> },
    ) => {
      state.isLoading = false
      state.attendance = payload.data
      state.totalCount = payload.totalCount
      state.totalPages = payload.totalPages
      state.noAttendances = payload.data.length < 1
    },
    [getAttendances.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createAttendance.pending.type]: (state) => {
      state.isLoading = true
    },
    [createAttendance.fulfilled.type]: (
      state,
      { payload }: { payload: { userId: number; data: IPostAttendanceRequestResponse } },
    ) => {
      state.isLoading = false
      state.attendance = state.attendance.map((a) => {
        if (a.id === payload.userId) {
          const inputExists = a.workScheduleInfos.find((wsi) =>
            areDatesSameDay(wsi.date, payload.data.startDate),
          )
          if (inputExists) {
            a.workScheduleInfos.map((wsi) => {
              if (areDatesSameDay(wsi.date, payload.data.startDate)) {
                wsi.workScheduleAttendanceInfo = payload.data
                return wsi
              }
              return wsi
            })
            return a
          } else {
            // ako ne postoji za odabrani dan
            a.workScheduleInfos.push({
              date: toStartOfUtcDay(payload.data.startDate).toISOString(),
              workSchedule: null,
              workScheduleAttendanceInfo: payload.data,
            })
            return a
          }
        }
        return a
      })

      toast.success('Attendance added')
    },
    [createAttendance.rejected.type]: (state, { payload }) => {
      state.isLoading = false

      handleErrors(payload)
    },
    [createMultipleAttendance.pending.type]: (state) => {
      state.isLoading = true
    },
    [createMultipleAttendance.fulfilled.type]: (
      state,
      {
        payload,
      }: {
        payload: { userIds: number[]; data: IPostAttendanceRequestResponse[] }
      },
    ) => {
      state.attendance = state.attendance.map((a) => {
        if (payload.userIds.includes(a.id)) {
          if (a.workScheduleInfos.length) {
            if (
              a.workScheduleInfos.some((wsi) =>
                areDatesSameDay(wsi.date, payload.data[0].startDate),
              )
            ) {
              a.workScheduleInfos = a.workScheduleInfos.map((wsi) => {
                if (areDatesSameDay(wsi.date, payload.data[0].startDate)) {
                  wsi.workScheduleAttendanceInfo = { ...payload.data[0], id: Number(v4()) }
                  return wsi
                }
                return wsi
              })
            } else {
              a.workScheduleInfos.push({
                date: toStartOfUtcDay(payload.data[0].startDate).toISOString(),
                workSchedule: null,
                workScheduleAttendanceInfo: payload.data[0],
              })
            }
          } else {
            a.workScheduleInfos.push({
              date: toStartOfUtcDay(payload.data[0].startDate).toISOString(),
              workSchedule: null,
              workScheduleAttendanceInfo: payload.data[0],
            })
          }
          return a
        }
        return a
      })

      // state.attendance = state.attendance.map((a) => {
      //   if (payload.userIds.includes(a.id)) {
      //     if (a.workScheduleInfos.length) {
      //       a.workScheduleInfos = a.workScheduleInfos.map((wsi) => {
      //         if (areDatesSameDay(wsi.date, payload.data[0].startDate)) {
      //           wsi.workScheduleAttendanceInfo = payload.data[0]
      //           return wsi
      //         } else {
      //           return wsi
      //         }
      //       })
      //     } else {
      //       a.workScheduleInfos.push({
      //         date: toStartOfUtcDay(payload.data[0].startDate).toISOString(),
      //         workSchedule: null,
      //         workScheduleAttendanceInfo: payload.data[0],
      //       })
      //     }
      //     return a
      //   } else {
      //     return a
      //   }
      // })
      state.isLoading = false
      toast.success('Attendance added')
    },
    [createMultipleAttendance.rejected.type]: (state, { payload }) => {
      state.isLoading = false

      handleErrors(payload)
    },
    [deleteAttendance.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteAttendance.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.attendance = state.attendance.map((a) => {
        if (a.id !== payload.userId) {
          return a
        } else {
          return {
            ...a,
            workScheduleInfos: a.workScheduleInfos.filter(
              (s) => s.workScheduleAttendanceInfo?.id !== payload.id,
            ),
          }
        }
      })
      toast.success('Attendance deleted')
    },
    [deleteAttendance.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [importAttendances.pending.type]: (state) => {
      state.isLoading = true
    },
    [importAttendances.fulfilled.type]: (state) => {
      state.isLoading = false
    },
    [importAttendances.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearAttendances } = attendanceSlice.actions
export default attendanceSlice.reducer
